import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Save, Plus, Trash2, Phone, Mail, Users, Wrench, MessageSquareQuote,
  FileText, Inbox, Loader2, ExternalLink, ShieldAlert, LayoutList, UserCog,
  ArrowUp, ArrowDown, Shield, ListOrdered, Handshake, Link2, Copy, Send, CheckCircle2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";
import PdfUpload from "../../components/PdfUpload";
import RichText from "../../components/RichText";
import { APPR_DEFAULTS, PALETTE_DEFAULT, PALETTE_KEYS, PALETTE_LABELS, BG_DEFAULTS } from "../../lib/appearance";

const inp = "w-full bg-white border border-[var(--mf-line)] px-3 py-2 text-sm focus:border-[var(--mf-red)] transition-colors";
const lbl = "block text-xs font-semibold uppercase tracking-wider text-[var(--mf-grey)] mb-1.5";

function Field({ label, value, onChange, textarea, rows = 3, placeholder, type = "text" }) {
  return (
    <div>
      <label className={lbl}>{label}</label>
      {textarea ? (
        <textarea className={inp} rows={rows} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={inp} type={type} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function moveArr(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}

function RowTools({ i, len, onUp, onDown, onRemove }) {
  return (
    <div className="flex gap-1 shrink-0">
      <button type="button" onClick={onUp} disabled={i === 0} className="p-1.5 border border-[var(--mf-line)] disabled:opacity-30 hover:bg-[var(--mf-mist)]"><ArrowUp size={14} /></button>
      <button type="button" onClick={onDown} disabled={i === len - 1} className="p-1.5 border border-[var(--mf-line)] disabled:opacity-30 hover:bg-[var(--mf-mist)]"><ArrowDown size={14} /></button>
      <button type="button" onClick={onRemove} className="p-1.5 border border-[var(--mf-red)]/40 text-[var(--mf-red)] hover:bg-[var(--mf-red)]/5"><Trash2 size={14} /></button>
    </div>
  );
}

function ArrayEditor({ label, items = [], onChange, fields, empty }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className={lbl + " !mb-0"}>{label}</span>
        <button type="button" onClick={() => onChange([...(items || []), { ...empty }])} className="text-xs inline-flex items-center gap-1 border border-[var(--mf-line)] px-3 py-1.5 hover:bg-[var(--mf-mist)]"><Plus size={13} /> Hinzufügen</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((it, i) => (
          <div key={i} className="border border-[var(--mf-line)] p-3 bg-[var(--mf-mist)]">
            <div className="flex justify-between items-start gap-3 mb-2">
              <span className="text-xs text-[var(--mf-grey)] font-semibold">#{i + 1}</span>
              <RowTools i={i} len={items.length} onUp={() => onChange(moveArr(items, i, -1))} onDown={() => onChange(moveArr(items, i, 1))} onRemove={() => onChange(items.filter((_, x) => x !== i))} />
            </div>
            <div className="space-y-2">
              {fields.map((f) => (
                f.rich ? (
                  <RichText key={f.key} label={f.label} value={it[f.key]} onChange={(v) => onChange(items.map((x, xi) => xi === i ? { ...x, [f.key]: v } : x))} />
                ) : (
                  <Field key={f.key} label={f.label} textarea={f.textarea} rows={f.rows}
                    value={it[f.key]} onChange={(v) => onChange(items.map((x, xi) => xi === i ? { ...x, [f.key]: v } : x))} />
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StringListEditor({ label, items = [], onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className={lbl + " !mb-0"}>{label}</span>
        <button type="button" onClick={() => onChange([...(items || []), ""])} className="text-xs inline-flex items-center gap-1 border border-[var(--mf-line)] px-3 py-1.5 hover:bg-[var(--mf-mist)]"><Plus size={13} /> Hinzufügen</button>
      </div>
      <div className="space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input className={inp} value={v} onChange={(e) => onChange(items.map((x, xi) => xi === i ? e.target.value : x))} />
            <RowTools i={i} len={items.length} onUp={() => onChange(moveArr(items, i, -1))} onDown={() => onChange(moveArr(items, i, 1))} onRemove={() => onChange(items.filter((_, x) => x !== i))} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerListEditor({ label, items = [], onChange }) {
  const norm = (items || []).map((it) =>
    typeof it === "string" ? { name: it, logo: "" } : { name: it?.name || "", logo: it?.logo || "" }
  );
  const update = (i, p) => onChange(norm.map((x, xi) => (xi === i ? { ...x, ...p } : x)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={lbl + " !mb-0"}>{label}</span>
        <button type="button" onClick={() => onChange([...norm, { name: "", logo: "" }])} className="text-xs inline-flex items-center gap-1 border border-[var(--mf-line)] px-3 py-1.5 hover:bg-[var(--mf-mist)]"><Plus size={13} /> Hinzufügen</button>
      </div>
      <p className="text-xs text-[var(--mf-grey)] mb-3">Logo hochladen ODER eine Logo-URL von der Hersteller-Website einfügen (z. B. …/logo.svg). Ist ein Logo vorhanden, wird es im Marquee angezeigt — sonst der Name. Tipp: Auf dunklem Hintergrund eine helle/weiße Logo-Version verwenden.</p>
      <div className="space-y-3">
        {norm.map((p, i) => (
          <div key={i} data-testid={`partner-row-${i}`} className="border border-[var(--mf-line)] p-3 flex gap-3 items-start">
            <div className="flex-1 space-y-3 min-w-0">
              <input className={inp} placeholder="Name" value={p.name} onChange={(e) => update(i, { name: e.target.value })} data-testid={`partner-name-${i}`} />
              <input className={inp} placeholder="Logo-URL (https://…/logo.svg)" value={/^https?:\/\//i.test(p.logo || "") ? p.logo : ""} onChange={(e) => update(i, { logo: e.target.value })} data-testid={`partner-logo-url-${i}`} />
              <ImageUpload label="… oder Logo hochladen" value={/^https?:\/\//i.test(p.logo || "") ? "" : p.logo} onChange={(v) => update(i, { logo: v })} />
            </div>
            <RowTools i={i} len={norm.length} onUp={() => onChange(moveArr(norm, i, -1))} onDown={() => onChange(moveArr(norm, i, 1))} onRemove={() => onChange(norm.filter((_, x) => x !== i))} />
          </div>
        ))}
      </div>
    </div>
  );
}

function LinkedInPostsEditor({ items = [], onChange }) {
  const list = items || [];
  const update = (i, v) => onChange(list.map((x, xi) => (xi === i ? v : x)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={lbl + " !mb-0"}>LinkedIn-Beiträge (Slideshow)</span>
        <button type="button" data-testid="linkedin-add" onClick={() => onChange([...list, ""])} className="text-xs inline-flex items-center gap-1 border border-[var(--mf-line)] px-3 py-1.5 hover:bg-[var(--mf-mist)]"><Plus size={13} /> Beitrag hinzufügen</button>
      </div>
      <p className="text-xs text-[var(--mf-grey)] mb-3">Bei LinkedIn beim Beitrag auf „…" → „Diesen Beitrag einbetten" klicken und den kompletten &lt;iframe…&gt;-Code hier einfügen.</p>
      <div className="space-y-3">
        {list.map((code, i) => (
          <div key={i} data-testid={`linkedin-post-row-${i}`} className="border border-[var(--mf-line)] p-3 flex gap-3 items-start">
            <textarea
              value={code}
              onChange={(e) => update(i, e.target.value)}
              rows={3}
              placeholder={'<iframe src="https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:..." ...></iframe>'}
              className="flex-1 min-w-0 bg-white border border-[var(--mf-line)] px-3 py-2 text-xs font-mono focus:border-[var(--mf-red)] focus:outline-none"
              data-testid={`linkedin-post-input-${i}`}
            />
            <RowTools i={i} len={list.length} onUp={() => onChange(moveArr(list, i, -1))} onDown={() => onChange(moveArr(list, i, 1))} onRemove={() => onChange(list.filter((_, x) => x !== i))} />
          </div>
        ))}
        {!list.length && <p className="text-sm text-[var(--mf-grey)] italic">Noch keine Beiträge — füge deinen ersten LinkedIn-Embed-Code hinzu.</p>}
      </div>
    </div>
  );
}



function Stepper({ label, value, onChange, step = 4, min = 0, max = 400, name = "" }) {
  const set = (v) => { if (Number.isNaN(v)) return; onChange(Math.max(min, Math.min(max, v))); };
  return (
    <div>
      <span className={lbl}>{label}</span>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`stepper-${name}-minus`} onClick={() => set(value - step)} className="w-9 h-9 border border-[var(--mf-line)] bg-white flex items-center justify-center hover:bg-[var(--mf-mist)] text-xl leading-none">−</button>
        <div className="relative">
          <input type="number" data-testid={`stepper-${name}-input`} value={value} onChange={(e) => set(parseInt(e.target.value, 10))} className="w-24 bg-white border border-[var(--mf-line)] pl-3 pr-7 py-2 text-sm text-center focus:border-[var(--mf-red)]" />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[var(--mf-grey)] pointer-events-none">px</span>
        </div>
        <button type="button" data-testid={`stepper-${name}-plus`} onClick={() => set(value + step)} className="w-9 h-9 border border-[var(--mf-line)] bg-white flex items-center justify-center hover:bg-[var(--mf-mist)] text-xl leading-none">+</button>
      </div>
    </div>
  );
}

function SizeControls({ sectionKey, settings, setSettings, onSave, saving }) {
  const d = APPR_DEFAULTS[sectionKey] || { h: 88, t: 16 };
  const cur = (settings.appearance || {})[sectionKey] || {};
  const h = Number.isFinite(cur.h) ? cur.h : d.h;
  const t = Number.isFinite(cur.t) ? cur.t : d.t;
  const palette = { ...PALETTE_DEFAULT, ...(settings.palette || {}) };
  const bgKey = cur.bg && palette[cur.bg] ? cur.bg : (BG_DEFAULTS[sectionKey] || "weiss");
  const upd = (patch) => setSettings((s) => ({ ...s, appearance: { ...(s.appearance || {}), [sectionKey]: { ...((s.appearance || {})[sectionKey] || {}), h, t, ...patch } } }));
  return (
    <div data-testid={`size-controls-${sectionKey}`} className="mb-5 flex flex-wrap items-end gap-6 p-4 bg-[var(--mf-mist)] border border-[var(--mf-line)]">
      <Stepper label="Höhe (Abstand oben/unten)" name={`${sectionKey}-h`} value={h} step={4} min={0} max={400} onChange={(v) => upd({ h: v })} />
      <Stepper label="Textgröße (Basis)" name={`${sectionKey}-t`} value={t} step={1} min={10} max={40} onChange={(v) => upd({ t: v })} />
      <div>
        <span className="block text-xs font-semibold uppercase tracking-wider text-[var(--mf-grey)] mb-2">Hintergrundfarbe</span>
        <div className="flex items-center gap-2">
          {PALETTE_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              title={PALETTE_LABELS[k]}
              data-testid={`bg-${sectionKey}-${k}`}
              onClick={() => upd({ bg: k })}
              className={`w-8 h-8 rounded-sm border-2 transition-all ${bgKey === k ? "border-[var(--mf-red)] scale-110 shadow" : "border-[var(--mf-line)] hover:scale-105"}`}
              style={{ backgroundColor: palette[k] }}
            />
          ))}
        </div>
      </div>
      {onSave && (
        <button type="button" onClick={onSave} disabled={saving} data-testid={`size-save-${sectionKey}`} className="mf-cta text-sm py-2.5 px-5 disabled:opacity-60 ml-auto">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
        </button>
      )}
    </div>
  );
}

function PaletteEditor({ settings, setSettings }) {
  const palette = { ...PALETTE_DEFAULT, ...(settings.palette || {}) };
  const upd = (k, v) => setSettings((s) => ({ ...s, palette: { ...PALETTE_DEFAULT, ...(s.palette || {}), [k]: v } }));
  return (
    <div data-testid="palette-editor" className="p-5 bg-[var(--mf-mist)] border border-[var(--mf-line)]">
      <h3 className="font-head text-lg text-[var(--mf-ink)]">Farbpalette</h3>
      <p className="text-xs text-[var(--mf-grey)] mt-1 mb-4">Fünf Farben — passe die Töne beliebig an. Wähle danach pro Abschnitt eine davon als Hintergrund (Textfarbe passt sich bei dunklen Tönen automatisch an). Nach dem Ändern oben „Speichern“ klicken.</p>
      <div className="flex flex-wrap gap-5">
        {PALETTE_KEYS.map((k) => (
          <div key={k}>
            <span className="block text-xs font-semibold uppercase tracking-wider text-[var(--mf-grey)] mb-2">{PALETTE_LABELS[k]}</span>
            <div className="flex items-center gap-2">
              <input type="color" value={palette[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`palette-${k}`} className="w-10 h-10 p-0 border border-[var(--mf-line)] cursor-pointer bg-white" />
              <input type="text" value={palette[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`palette-hex-${k}`} className="w-24 px-2 py-1.5 text-sm border border-[var(--mf-line)] bg-white text-[var(--mf-ink)]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("inhalte");
  const [settings, setSettings] = useState(null);
  const [zielgruppen, setZielgruppen] = useState([]);
  const [leistungen, setLeistungen] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [voices, setVoices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const NAV = [
    { id: "inhalte", label: "Seiteninhalte", icon: LayoutList },
    { id: "abschnitte", label: "Reihenfolge", icon: ListOrdered },
    { id: "zielgruppen", label: "Zielgruppen", icon: Users },
    { id: "leistungen", label: "Leistungen", icon: Wrench },
    { id: "kundenstimmen", label: "Kundenstimmen", icon: MessageSquareQuote },
    { id: "partnerstimmen", label: "Partnerstimmen", icon: Handshake },
    { id: "kontakt", label: "Kontakt & E-Mail", icon: Phone },
    { id: "texte", label: "Rechtstexte", icon: FileText },
    { id: "anfragen", label: "Anfragen", icon: Inbox },
    ...(isAdmin ? [{ id: "benutzer", label: "Benutzer", icon: UserCog }] : []),
  ];

  const load = () => api.get("/content").then((r) => {
    setSettings(r.data.settings);
    setZielgruppen(r.data.zielgruppen);
    setLeistungen(r.data.leistungen);
    setTestimonials(r.data.testimonials);
  });

  const loadVoices = () => api.get("/admin/voices").then((r) => setVoices(r.data)).catch(() => {});

  useEffect(() => {
    load();
    loadVoices();
    api.get("/admin/contact-requests").then((r) => setRequests(r.data)).catch(() => {});
    if (isAdmin) api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  const patch = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const patchObj = (k, sub, v) => setSettings((s) => ({ ...s, [k]: { ...s[k], [sub]: v } }));

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put("/admin/settings", settings); toast.success("Änderungen gespeichert."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  if (!settings) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--mf-grey)]" /></div>;

  return (
    <div className="min-h-screen bg-[var(--mf-mist)] flex flex-col lg:flex-row">
      <aside className="lg:w-64 bg-[var(--mf-ink)] text-white flex flex-col shrink-0">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src="/mfmed-logo-light.png" alt="MF MED" className="h-8" />
          <span className="font-head font-bold">Verwaltung</span>
        </div>
        <nav className="flex-1 p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {NAV.map((n) => {
            const I = n.icon;
            return (
              <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => setTab(n.id)}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left whitespace-nowrap transition-colors ${tab === n.id ? "bg-[var(--mf-red)] text-white" : "text-white/70 hover:bg-white/10"}`}>
                <I size={17} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-2">{user?.name} · {user?.role === "admin" ? "Administrator" : "Bediener"}</div>
          <div className="flex gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 text-xs border border-white/20 py-2 hover:bg-white/10"><ExternalLink size={13} /> Seite</a>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex-1 flex items-center justify-center gap-1.5 text-xs border border-white/20 py-2 hover:bg-white/10"><LogOut size={13} /> Abmelden</button>
          </div>
        </div>
      </aside>

      <main className="flex-1 p-5 lg:p-10 overflow-y-auto">
        <div className="max-w-4xl">
          {tab === "inhalte" && (
            <Section title="Seiteninhalte" onSave={saveSettings} saving={savingSettings}>
              <div className="space-y-8">
                <PaletteEditor settings={settings} setSettings={setSettings} />

                <Group title="Logo (Kopfbereich)">
                  <div className="flex flex-wrap items-end gap-6">
                    <Stepper label="Logo-Größe (Desktop / Laptop)" name="logo" value={Number.isFinite(settings.logo_height) ? settings.logo_height : 56} step={2} min={28} max={96} onChange={(v) => patch("logo_height", v)} />
                    <Stepper label="Logo-Größe (Tablet / Smartphone)" name="logo-mobile" value={Number.isFinite(settings.logo_height_mobile) ? settings.logo_height_mobile : 40} step={2} min={24} max={72} onChange={(v) => patch("logo_height_mobile", v)} />
                  </div>
                  <p className="text-xs text-[var(--mf-grey)]">Stellt die Höhe des Logos im Kopfbereich ein — separat für große Bildschirme (Desktop/Laptop ab 1024&nbsp;px) und für kleinere Geräte (Tablet &amp; Smartphone). Nach dem Ändern oben „Speichern“ klicken.</p>
                </Group>

                <Group title="Titelbereich (Hero)">
                  <SizeControls sectionKey="hero" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                  <Field label="Kleine Überschrift" value={settings.hero.eyebrow} onChange={(v) => patchObj("hero", "eyebrow", v)} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Überschrift Zeile 1 (stark)" value={settings.hero.h1_line1} onChange={(v) => patchObj("hero", "h1_line1", v)} />
                    <Field label="Überschrift Zeile 2" value={settings.hero.h1_line2} onChange={(v) => patchObj("hero", "h1_line2", v)} />
                  </div>
                  <Field label="Einleitungssatz" textarea value={settings.hero.subheadline} onChange={(v) => patchObj("hero", "subheadline", v)} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Button 1 (Anrufen)" value={settings.hero.cta_primary} onChange={(v) => patchObj("hero", "cta_primary", v)} />
                    <Field label="Button 2 (sekundär)" value={settings.hero.cta_secondary} onChange={(v) => patchObj("hero", "cta_secondary", v)} />
                  </div>
                </Group>

                {(() => {
                  const groups = {
                    problem: (
                      <Group key="problem" title="Problembereich">
                        <SizeControls sectionKey="problem" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                        <ArrayEditor label="Situationen der Betreiber" items={settings.problems} empty={{ text: "" }}
                          onChange={(v) => patch("problems", v)} fields={[{ key: "text", label: "Aussage", textarea: true }]} />
                      </Group>
                    ),
                    regulatorik: (
                      <Group key="regulatorik" title="Betreiberpflicht / Regulatorik">
                        <SizeControls sectionKey="regulatorik" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                        <RichText label="Einleitung" value={settings.regulatorik.intro} onChange={(v) => patchObj("regulatorik", "intro", v)} />
                        <ArrayEditor label="Kapitel" items={settings.regulatorik.chapters} empty={{ no: "", title: "", text: "" }}
                          onChange={(v) => patchObj("regulatorik", "chapters", v)}
                          fields={[{ key: "no", label: "Nummer" }, { key: "title", label: "Titel" }, { key: "text", label: "Text", textarea: true }]} />
                      </Group>
                    ),
                    ablauf: (
                      <Group key="ablauf" title="Ablauf">
                        <SizeControls sectionKey="ablauf" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                        <ArrayEditor label="Schritte" items={settings.process} empty={{ no: "", title: "", text: "" }}
                          onChange={(v) => patch("process", v)}
                          fields={[{ key: "no", label: "Nummer" }, { key: "title", label: "Titel" }, { key: "text", label: "Text", textarea: true }]} />
                      </Group>
                    ),
                    inhaber: (
                      <Group key="inhaber" title="Über Matthias Fenchel">
                        <SizeControls sectionKey="inhaber" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                        <div className="grid sm:grid-cols-2 gap-4">
                          <Field label="Name" value={settings.about.name} onChange={(v) => patchObj("about", "name", v)} />
                          <Field label="Rolle" value={settings.about.role} onChange={(v) => patchObj("about", "role", v)} />
                        </div>
                        <ImageUpload label="Foto Matthias Fenchel" value={settings.about.image} onChange={(v) => patchObj("about", "image", v)} />
                        <RichText label="Text (Ich-Form)" value={settings.about.text} onChange={(v) => patchObj("about", "text", v)} />
                        <Field label="LinkedIn-Profil (URL)" value={settings.about.linkedin} onChange={(v) => patchObj("about", "linkedin", v)} placeholder="https://www.linkedin.com/in/…" />
                        <StringListEditor label="Qualifikationen" items={settings.about.qualifications} onChange={(v) => patchObj("about", "qualifications", v)} />
                      </Group>
                    ),
                    faq: (
                      <Group key="faq" title="FAQ">
                        <SizeControls sectionKey="faq" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                        <ArrayEditor label="Fragen & Antworten" items={settings.faq} empty={{ q: "", a: "" }}
                          onChange={(v) => patch("faq", v)}
                          fields={[{ key: "q", label: "Frage" }, { key: "a", label: "Antwort", rich: true }]} />
                      </Group>
                    ),
                    partner: (
                      <Group key="partner" title="Partner (Marquee)">
                        <SizeControls sectionKey="partner" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
                        <PartnerListEditor label="Hersteller / Software-Partner" items={settings.partners} onChange={(v) => patch("partners", v)} />
                      </Group>
                    ),
                    linkedin: (
                      <Group key="linkedin" title="LinkedIn-Beiträge (über dem Footer)">
                        <LinkedInPostsEditor items={settings.linkedin_posts} onChange={(v) => patch("linkedin_posts", v)} />
                      </Group>
                    ),
                  };
                  const order = settings.section_order && settings.section_order.length ? settings.section_order : Object.keys(groups);
                  const keys = [...order.filter((k) => groups[k]), ...Object.keys(groups).filter((k) => !order.includes(k))];
                  return keys.map((k) => groups[k]);
                })()}
              </div>
            </Section>
          )}

          {tab === "kontakt" && (
            <Section title="Kontakt & E-Mail" onSave={saveSettings} saving={savingSettings}>
              <SizeControls sectionKey="kontakt" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telefon (Anzeige)" value={settings.contact.phone} onChange={(v) => patchObj("contact", "phone", v)} />
                <Field label="Telefon (Wählnummer)" value={settings.contact.phone_dial} onChange={(v) => patchObj("contact", "phone_dial", v)} placeholder="+4962515504920" />
                <Field label="E-Mail" value={settings.contact.email} onChange={(v) => patchObj("contact", "email", v)} />
                <Field label="WhatsApp-Nummer" value={settings.contact.whatsapp} onChange={(v) => patchObj("contact", "whatsapp", v)} placeholder="+4962515504920" />
                <Field label="Adresse Zeile 1" value={settings.contact.address_line1} onChange={(v) => patchObj("contact", "address_line1", v)} />
                <Field label="Adresse Zeile 2" value={settings.contact.address_line2} onChange={(v) => patchObj("contact", "address_line2", v)} />
                <Field label="Adresse Zeile 3" value={settings.contact.address_line3} onChange={(v) => patchObj("contact", "address_line3", v)} />
                <Field label="Einsatzgebiet" value={settings.contact.service_region} onChange={(v) => patchObj("contact", "service_region", v)} />
                <Field label="Servicezeiten" value={settings.contact.service_hours} onChange={(v) => patchObj("contact", "service_hours", v)} />
              </div>
              <div className="mt-6 pt-6 border-t border-[var(--mf-line)]">
                <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Mail size={16} className="text-[var(--mf-red)]" /> E-Mail-Vorlage (Kontakt-Button)</h3>
                <div className="space-y-4">
                  <Field label="Betreff" value={settings.email_template.subject} onChange={(v) => patchObj("email_template", "subject", v)} />
                  <Field label="Text" textarea rows={9} value={settings.email_template.body} onChange={(v) => patchObj("email_template", "body", v)} />
                </div>
              </div>
            </Section>
          )}

          {tab === "zielgruppen" && (
            <>
              <SizeControls sectionKey="zielgruppen" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
              <ItemManager title="Zielgruppen" coll="zielgruppen" items={zielgruppen} setItems={setZielgruppen} reload={load} fields="basic" />
            </>
          )}
          {tab === "leistungen" && (
            <>
              <SizeControls sectionKey="leistungen" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
              <ItemManager title="Leistungen" coll="leistungen" items={leistungen} setItems={setLeistungen} reload={load} fields="full" />
            </>
          )}
          {tab === "kundenstimmen" && (
            <>
              <SizeControls sectionKey="kundenstimmen" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
              <VoiceManager vtype="kunde" voices={voices} reload={loadVoices} />
            </>
          )}
          {tab === "partnerstimmen" && (
            <>
              <SizeControls sectionKey="partnerstimmen" settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
              <VoiceManager vtype="partner" voices={voices} reload={loadVoices} />
            </>
          )}
          {tab === "abschnitte" && (
            <SectionOrderEditor settings={settings} setSettings={setSettings} onSave={saveSettings} saving={savingSettings} />
          )}
          {tab === "texte" && (
            <Section title="Rechtstexte" onSave={saveSettings} saving={savingSettings}>
              <div className="space-y-6">
                <RichText label="Impressum" value={settings.impressum} onChange={(v) => patch("impressum", v)} />
                <RichText label="Datenschutzerklärung" value={settings.datenschutz} onChange={(v) => patch("datenschutz", v)} />
                <div className="pt-6 border-t border-[var(--mf-line)] space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--mf-red)]">AGB / Widerruf (eigene Seite /agb)</p>
                  <RichText label="AGB – Text" value={settings.agb} onChange={(v) => patch("agb", v)} />
                  <PdfUpload label="AGB – PDF zum Download" value={settings.agb_pdf} onChange={(v) => patch("agb_pdf", v)} />
                  <div className="pt-4 border-t border-[var(--mf-line)] space-y-4">
                    <RichText label="Widerruf – Text" value={settings.widerruf} onChange={(v) => patch("widerruf", v)} />
                    <PdfUpload label="Widerruf – PDF zum Download" value={settings.widerruf_pdf} onChange={(v) => patch("widerruf_pdf", v)} />
                  </div>
                </div>
              </div>
            </Section>
          )}
          {tab === "anfragen" && (
            <Requests requests={requests} setRequests={setRequests} isAdmin={isAdmin} />
          )}
          {tab === "benutzer" && isAdmin && (
            <UserManager users={users} setUsers={setUsers} currentEmail={user?.email} />
          )}
        </div>
      </main>
    </div>
  );
}

function Section({ title, children, onSave, saving }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-head font-bold text-2xl tracking-tight text-[var(--mf-ink)]">{title}</h1>
        {onSave && (
          <button data-testid="save-settings" onClick={onSave} disabled={saving} className="mf-cta text-sm py-2.5 px-5 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
          </button>
        )}
      </div>
      <div className="bg-white border border-[var(--mf-line)] p-6">{children}</div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="border-b border-[var(--mf-line)] pb-8 last:border-0 last:pb-0">
      <h3 className="font-head font-bold text-lg text-[var(--mf-ink)] mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

const EMPTY_ITEM = { title: "", description: "", bullets: [], image: "", icon: "", order: 99 };

function ItemManager({ title, coll, items, setItems, reload, fields }) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const payload = { ...editing, bullets: editing.bullets || [] };
    try {
      if (editing.id) await api.put(`/admin/item/${coll}/${editing.id}`, payload);
      else await api.post(`/admin/item/${coll}`, payload);
      await reload(); setEditing(null); toast.success("Gespeichert.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Diesen Eintrag löschen?")) return;
    try { await api.delete(`/admin/item/${coll}/${id}`); await reload(); toast.success("Gelöscht."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i], b = items[j];
    try {
      await api.put(`/admin/item/${coll}/${a.id}`, { ...a, order: b.order ?? j });
      await api.put(`/admin/item/${coll}/${b.id}`, { ...b, order: a.order ?? i });
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-head font-bold text-2xl tracking-tight text-[var(--mf-ink)]">{title}</h1>
        <button data-testid={`add-${coll}`} onClick={() => setEditing({ ...EMPTY_ITEM })} className="mf-cta text-sm py-2.5 px-5"><Plus size={16} /> Neu</button>
      </div>
      <p className="text-sm text-[var(--mf-grey)] mb-4">Mit den Pfeilen ändern Sie die Reihenfolge auf der Website.</p>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`${coll}-row-${it.id}`} className="bg-white border border-[var(--mf-line)] p-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-head font-semibold text-[var(--mf-ink)]">{it.title}</div>
              <div className="text-sm text-[var(--mf-grey)] line-clamp-2 mt-1">{it.description}</div>
            </div>
            <div className="flex gap-2 shrink-0 items-center">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 border border-[var(--mf-line)] disabled:opacity-30 hover:bg-[var(--mf-mist)]"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 border border-[var(--mf-line)] disabled:opacity-30 hover:bg-[var(--mf-mist)]"><ArrowDown size={14} /></button>
              <button onClick={() => setEditing(it)} data-testid={`edit-${it.id}`} className="text-xs border border-[var(--mf-line)] px-3 py-2 hover:bg-[var(--mf-mist)]">Bearbeiten</button>
              <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-xs border border-[var(--mf-red)]/40 text-[var(--mf-red)] px-3 py-2 hover:bg-[var(--mf-red)]/5"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="item-editor">
            <h2 className="font-head font-bold text-xl mb-5">{editing.id ? "Bearbeiten" : "Neuer Eintrag"}</h2>
            <div className="space-y-4">
              <Field label="Titel" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <RichText label="Beschreibung" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
              <Field label="Icon (z. B. stethoscope, server, shield-check, activity)" value={editing.icon} onChange={(v) => setEditing({ ...editing, icon: v })} />
              <ImageUpload label="Bild (optional)" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
              {fields === "full" && (
                <Field label="Stichpunkte (eine Zeile pro Punkt)" textarea rows={4}
                  value={(editing.bullets || []).join("\n")}
                  onChange={(v) => setEditing({ ...editing, bullets: v.split("\n").filter((x) => x.trim()) })} />
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} disabled={busy} className="mf-cta flex-1 disabled:opacity-60" data-testid="item-save">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
              </button>
              <button onClick={() => setEditing(null)} className="mf-cta-ghost">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SECTION_LABELS = {
  zielgruppen: "Zielgruppen", leistungen: "Leistungen", regulatorik: "Betreiberpflicht / Regulatorik",
  problem: "Was Betreiber uns berichten", ablauf: "Ablauf", inhaber: "Über Matthias Fenchel",
  partner: "Partner (Marquee)", partnerstimmen: "Partnerstimmen", kundenstimmen: "Kundenstimmen",
  kontakt: "Kontakt", faq: "FAQ", linkedin: "LinkedIn-News",
};

function SectionOrderEditor({ settings, setSettings, onSave, saving }) {
  const saved = settings.section_order && settings.section_order.length
    ? settings.section_order
    : Object.keys(SECTION_LABELS);
  const order = [...saved, ...Object.keys(SECTION_LABELS).filter((k) => !saved.includes(k))];
  const move = (i, dir) => {
    const arr = moveArr(order, i, dir);
    setSettings((s) => ({ ...s, section_order: arr }));
  };
  return (
    <Section title="Reihenfolge der Abschnitte" onSave={onSave} saving={saving}>
      <p className="text-sm text-[var(--mf-grey)] mb-4">Ziehen Sie die Abschnitte mit den Pfeilen in die gewünschte Reihenfolge. Der Titelbereich (Hero) bleibt immer oben.</p>
      <div className="space-y-2">
        {order.map((key, i) => (
          <div key={key} data-testid={`section-row-${key}`} className="flex items-center justify-between bg-[var(--mf-mist)] border border-[var(--mf-line)] px-4 py-3">
            <span className="flex items-center gap-3"><span className="font-head font-bold text-[var(--mf-red)] tabular-nums">{String(i + 1).padStart(2, "0")}</span><span className="font-medium text-[var(--mf-ink)]">{SECTION_LABELS[key] || key}</span></span>
            <span className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 border border-[var(--mf-line)] bg-white disabled:opacity-30"><ArrowUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === order.length - 1} className="p-1.5 border border-[var(--mf-line)] bg-white disabled:opacity-30"><ArrowDown size={14} /></button>
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function VoiceManager({ vtype, voices, reload }) {
  const items = voices.filter((v) => v.type === vtype);
  const title = vtype === "kunde" ? "Kundenstimmen" : "Partnerstimmen";
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null); // {email,name} form state or result

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/voice/${editing.id}`, editing);
      else await api.post(`/admin/voice`, { ...editing, type: vtype });
      await reload(); setEditing(null); toast.success("Gespeichert.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Löschen?")) return;
    try { await api.delete(`/admin/voice/${id}`); await reload(); toast.success("Gelöscht."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePublish = async (t) => {
    try { await api.put(`/admin/voice/${t.id}`, { ...t, published: !t.published }); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const a = items[i], b = items[j];
    try {
      await api.put(`/admin/voice/${a.id}`, { ...a, order: b.order ?? j });
      await api.put(`/admin/voice/${b.id}`, { ...b, order: a.order ?? i });
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h1 className="font-head font-bold text-2xl tracking-tight text-[var(--mf-ink)]">{title}</h1>
        <div className="flex gap-2">
          <button data-testid={`invite-${vtype}`} onClick={() => setInvite({ email: "", name: "", url: "" })} className="mf-cta-ghost text-sm py-2.5 px-4"><Link2 size={16} /> Einladungs-Link</button>
          <button data-testid={`add-voice-${vtype}`} onClick={() => setEditing({ type: vtype, quote: "", author: "", role: "", image: "", published: true, order: 99 })} className="mf-cta text-sm py-2.5 px-5"><Plus size={16} /> Neu</button>
        </div>
      </div>
      <p className="text-sm text-[var(--mf-grey)] mb-4">Neue Einreichungen über den Link erscheinen hier zuerst als „Nicht veröffentlicht“ und müssen von Ihnen freigegeben werden.</p>

      <div className="space-y-3">
        {items.map((t, i) => (
          <div key={t.id} data-testid={`voice-row-${t.id}`} className="bg-white border border-[var(--mf-line)] p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${t.published ? "bg-[var(--mf-mist)] text-[var(--mf-grey)] border border-[var(--mf-line)]" : "bg-[var(--mf-red)] text-white"}`}>{t.published ? "Veröffentlicht" : "Zur Freigabe"}</span>
                {t.via_invite && <span className="text-[10px] text-[var(--mf-grey)]">via Link</span>}
              </div>
              <div className="text-sm text-[var(--mf-ink)]" dangerouslySetInnerHTML={{ __html: (t.quote || "").slice(0, 220) }} />
              <div className="text-xs text-[var(--mf-grey)] mt-1">{t.author} · {t.role}</div>
            </div>
            <div className="flex gap-2 shrink-0 items-center">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 border border-[var(--mf-line)] disabled:opacity-30 hover:bg-[var(--mf-mist)]"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 border border-[var(--mf-line)] disabled:opacity-30 hover:bg-[var(--mf-mist)]"><ArrowDown size={14} /></button>
              <button onClick={() => togglePublish(t)} data-testid={`publish-${t.id}`} className={`text-xs px-3 py-2 border ${t.published ? "border-[var(--mf-line)]" : "border-[var(--mf-red)] text-[var(--mf-red)]"} hover:bg-[var(--mf-mist)]`}>{t.published ? "Verbergen" : "Freigeben"}</button>
              <button onClick={() => setEditing(t)} className="text-xs border border-[var(--mf-line)] px-3 py-2 hover:bg-[var(--mf-mist)]">Bearbeiten</button>
              <button onClick={() => remove(t.id)} className="text-xs border border-[var(--mf-red)]/40 text-[var(--mf-red)] px-3 py-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="voice-editor">
            <h2 className="font-head font-bold text-xl mb-5">{editing.id ? "Bearbeiten" : `Neue ${title.slice(0, -1)}`}</h2>
            <div className="space-y-4">
              <RichText label="Zitat" value={editing.quote} onChange={(v) => setEditing({ ...editing, quote: v })} />
              <Field label={vtype === "partner" ? "Firma / Partner" : "Name / Einrichtung"} value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
              <Field label={vtype === "partner" ? "Hersteller / Software" : "Ort"} value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} />
              <ImageUpload label="Bild / Logo (optional)" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
              <label className="flex items-center gap-2 text-sm text-[var(--mf-ink)]"><input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Auf der Website veröffentlichen</label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} disabled={busy} className="mf-cta flex-1 disabled:opacity-60" data-testid="voice-save">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern</button>
              <button onClick={() => setEditing(null)} className="mf-cta-ghost">Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {invite && <InviteModal vtype={vtype} onClose={() => setInvite(null)} />}
    </div>
  );
}

function InviteModal({ vtype, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/invites`, { type: vtype, email, name, origin: window.location.origin });
      setResult(data);
      if (data.emailed) toast.success("Einladung per E-Mail gesendet.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(result.url); toast.success("Link kopiert."); };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        <h2 className="font-head font-bold text-xl mb-2">Einladungs-Link erstellen</h2>
        <p className="text-sm text-[var(--mf-grey)] mb-5">{vtype === "partner" ? "Für eine Partnerstimme" : "Für eine Kundenstimme"}. E-Mail ist optional — mit E-Mail wird der Link direkt versendet.</p>
        {!result ? (
          <div className="space-y-4">
            <Field label="Name (optional)" value={name} onChange={setName} />
            <Field label="E-Mail (optional)" value={email} onChange={setEmail} placeholder="name@firma.de" />
            <button onClick={create} disabled={busy} className="mf-cta w-full disabled:opacity-60" data-testid="invite-create">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Link erstellen{email ? " & senden" : ""}</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[var(--mf-ink)]"><CheckCircle2 size={18} className="text-[var(--mf-red)]" /> Link erstellt{result.emailed ? " und per E-Mail gesendet" : ""}.</div>
            <div className="flex gap-2">
              <input readOnly className={inp} value={result.url} data-testid="invite-url" />
              <button onClick={copy} className="mf-cta-ghost px-4" data-testid="invite-copy"><Copy size={16} /></button>
            </div>
            <button onClick={onClose} className="mf-cta-ghost w-full">Schließen</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Requests({ requests, setRequests, isAdmin }) {
  const remove = async (id) => {
    if (!window.confirm("Anfrage löschen?")) return;
    try { await api.delete(`/admin/contact-requests/${id}`); setRequests((r) => r.filter((x) => x.id !== id)); toast.success("Gelöscht."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h1 className="font-head font-bold text-2xl tracking-tight text-[var(--mf-ink)] mb-6">Kontaktanfragen ({requests.length})</h1>
      {requests.length === 0 && <div className="bg-white border border-[var(--mf-line)] p-8 text-center text-[var(--mf-grey)]">Noch keine Anfragen.</div>}
      <div className="space-y-3">
        {requests.map((r) => (
          <div key={r.id} className="bg-white border border-[var(--mf-line)] p-5" data-testid={`request-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-head font-semibold text-[var(--mf-ink)]">{r.name}</div>
                <div className="text-sm text-[var(--mf-grey)]">
                  {r.email && <a href={`mailto:${r.email}`} className="hover:text-[var(--mf-red)]">{r.email}</a>}
                  {r.email && r.phone && " · "}
                  {r.phone && <a href={`tel:${r.phone}`} className="hover:text-[var(--mf-red)]">{r.phone}</a>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[var(--mf-grey)]">{new Date(r.created_at).toLocaleString("de-DE")}</div>
                {isAdmin && <button onClick={() => remove(r.id)} className="mt-2 text-xs text-[var(--mf-red)] inline-flex items-center gap-1"><Trash2 size={13} /> Löschen</button>}
              </div>
            </div>
            <p className="mt-3 text-[var(--mf-ink-soft)] whitespace-pre-line border-t border-[var(--mf-line)] pt-3">{r.message}</p>
          </div>
        ))}
      </div>
      {!isAdmin && <div className="mt-4 text-xs text-[var(--mf-grey)] flex items-center gap-2"><ShieldAlert size={14} /> Löschen ist Administratoren vorbehalten.</div>}
    </div>
  );
}

function UserManager({ users, setUsers, currentEmail }) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const openNew = () => setEditing({ email: "", name: "", password: "", role: "user", responsibilities: "" });
  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) {
        const { data } = await api.put(`/admin/users/${editing.id}`, {
          name: editing.name, role: editing.role, responsibilities: editing.responsibilities, password: editing.password || "",
        });
        setUsers((u) => u.map((x) => x.id === data.id ? data : x));
      } else {
        const { data } = await api.post(`/admin/users`, editing);
        setUsers((u) => [...u, data]);
      }
      setEditing(null); toast.success("Gespeichert.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Benutzer ${u.email} löschen?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); setUsers((x) => x.filter((y) => y.id !== u.id)); toast.success("Gelöscht."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-head font-bold text-2xl tracking-tight text-[var(--mf-ink)]">Benutzer</h1>
        <button data-testid="add-user" onClick={openNew} className="mf-cta text-sm py-2.5 px-5"><Plus size={16} /> Benutzer anlegen</button>
      </div>
      <p className="text-sm text-[var(--mf-grey)] mb-4">Legen Sie Konten an, vergeben Sie eine Rolle und beschreiben Sie die Verantwortlichkeit.</p>

      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-white border border-[var(--mf-line)] p-4 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-head font-semibold text-[var(--mf-ink)]">{u.name || u.email}</span>
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 ${u.role === "admin" ? "bg-[var(--mf-red)] text-white" : "bg-[var(--mf-mist)] text-[var(--mf-grey)] border border-[var(--mf-line)]"}`}>
                  {u.role === "admin" ? <Shield size={11} /> : null}{u.role === "admin" ? "Administrator" : "Bediener"}
                </span>
              </div>
              <div className="text-sm text-[var(--mf-grey)] mt-1">{u.email}</div>
              {u.responsibilities && <div className="text-sm text-[var(--mf-ink-soft)] mt-1">Verantwortlich: {u.responsibilities}</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing({ ...u, password: "" })} className="text-xs border border-[var(--mf-line)] px-3 py-2 hover:bg-[var(--mf-mist)]">Bearbeiten</button>
              <button onClick={() => remove(u)} disabled={u.email === currentEmail} data-testid={`delete-user-${u.id}`}
                className="text-xs border border-[var(--mf-red)]/40 text-[var(--mf-red)] px-3 py-2 disabled:opacity-30 hover:bg-[var(--mf-red)]/5"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="user-editor">
            <h2 className="font-head font-bold text-xl mb-5">{editing.id ? "Benutzer bearbeiten" : "Benutzer anlegen"}</h2>
            <div className="space-y-4">
              <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              {!editing.id && <Field label="E-Mail" type="email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />}
              <Field label={editing.id ? "Neues Passwort (leer = unverändert)" : "Passwort (min. 6 Zeichen)"} type="password" value={editing.password} onChange={(v) => setEditing({ ...editing, password: v })} />
              <div>
                <label className={lbl}>Rolle</label>
                <select className={inp} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="user-role-select">
                  <option value="user">Bediener (Inhalte pflegen)</option>
                  <option value="admin">Administrator (voller Zugriff)</option>
                </select>
              </div>
              <Field label="Verantwortlichkeit" textarea rows={2} value={editing.responsibilities} onChange={(v) => setEditing({ ...editing, responsibilities: v })} placeholder="z. B. Leistungen & Zielgruppen" />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} disabled={busy} className="mf-cta flex-1 disabled:opacity-60" data-testid="user-save">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern</button>
              <button onClick={() => setEditing(null)} className="mf-cta-ghost">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
